import { PureComponent } from 'react';
import { View, Image, TouchableOpacity } from 'react-native';
import { Text, Popup, Button, TextInput, LabelledView, PickerInput, Loading } from '@symbolic/rn-lib';
import { connect } from '@symbolic/redux';
import { resourceActions } from '~/redux';
import { api, products } from '@symbolic/lib';
import _ from 'lodash';
import AceEditor from 'react-ace';

// Meljac specific imports
import getEngravingWidthLimitData from '~/helpers/meljac/get-engraving-width-limit';
import CustomEngravingsInput from '~/helpers/meljac/custom-engravings-input';
import GetDisclaimer from '~/helpers/get-disclaimer';

// Components and assets
import K from '~/k';
import ProductRule from '~/components/product-rule';
import FileInput from '~/components/file-input';
import handleFileUpload from '~/helpers/upload-file-helper';
import thumbnailPlaceholder from '~/assets/thumbnail-placeholder.png';
import trashIcon from '~/assets/trash-icon.png';
import { publishLevelOptions } from '../../helpers/publish-level-options';

// Ace editor configuration
import 'ace-builds/src-noconflict/mode-json';
import 'ace-builds/src-noconflict/theme-github';
import 'ace-builds/src-noconflict/ext-language_tools';

// Extract meljac functions once
const { getCleanedEngravings, getEngravingHeightLimit } = products.meljac;

class ProductOptionPopup extends PureComponent {
  constructor(props) {
    super(props);

    this.state = {
      data: this.formatInitialData(props.productOption?.data),
      classifications: props.productOption?.classifications || {},
      publishLevel: props.productOption?.publishLevel,
      productPricingRuleId: props.productOption?.productPricingRuleId,
      image: null,
      uploading: false,
      uploadWasSuccessful: false,
      uploadHasFailed: false,
      isLoadingClassifierData: true,
    };

    this.handleInputChangeDebounced = _.debounce(this.handleInputChange, 150);
  }

  // Utility function to format initial data
  formatInitialData = (data) => {
    if (!data) return '';
    if (typeof data === 'object') {
      return _.keys(data).length ? JSON.stringify(data) : '';
    }
    return data;
  };

  // Utility function to safely parse JSON
  safeJsonParse = (value) => {
    try {
      return JSON.parse(value);
    } catch (err) {
      console.error('Invalid JSON:', err);
      return value;
    }
  };

  // Utility function to update product option
  updateProductOptionAndTrack = async (props) => {
    await this.props.updateProductOption({
      id: this.props.productOption.id,
      props
    });

    const updatedProductOption = { ...this.props.productOption, ...props };
    this.props.trackProductOptions({ productOptions: [updatedProductOption] });
  };

  async componentDidMount() {
    try {
      const [productOptionClassifierGroups, productOptionClassifiers] = await Promise.all([
        api.get('productOptionClassifierGroups', {
          where: { orgId: this.props.session.activeOrg.id }
        }),
        api.get('productOptionClassifiers', {
          where: { orgId: this.props.session.activeOrg.id }
        })
      ]);

      this.props.trackProductOptionClassifierGroups({ productOptionClassifierGroups });
      this.props.trackProductOptionClassifiers({ productOptionClassifiers });

      this.setState({ isLoadingClassifierData: false });
    } catch (error) {
      console.error('Error loading classifier data:', error);
      this.setState({ isLoadingClassifierData: false });
    }
  }

  componentDidUpdate(prevProps) {
    if (this.props.mode === 'admin') {
      const prevClassifications = prevProps.productOption?.classifications;
      const currentClassifications = this.props.productOption?.classifications;

      if (!_.isEqual(prevClassifications, currentClassifications)) {
        this.setState({
          classifications: currentClassifications || {}
        });
      }
    }
  }

  close = () => {
    this.props.onClose();
    this.setState({uploading: false, uploadWasSuccessful: false, uploadHasFailed: false});
  };

  handleTitleChange = ({value}) => this.props.updateProductOption({id: this.props.productOption.id, props: {title: value}});

  handleProductOptionPropsChange = async ({ propKey, value }) => {
    // Update local state immediately for UI responsiveness
    this.setState({ [propKey]: value });

    // Use utility function for consistent update pattern
    await this.updateProductOptionAndTrack({ [propKey]: value });
  };

  archive = async () => {
    const newArchivedState = this.props.productOption.archived === 1 ? 0 : 1;
    await this.updateProductOptionAndTrack({ archived: newArchivedState });
  };

  handleInputChange = ({ value, index, inputIndex, hitApi = true }) => {
    const { productInstance, productProperty } = this.props;

    const properties = _.get(productInstance, 'properties', {});
    const propertyData = _.get(properties, `${productProperty.id}`, {});
    const path = (inputIndex || inputIndex === 0) ? `[${index}[${inputIndex}]` : `[${index}]`;

    const updatedPropertyData = _.set(propertyData, `${productProperty.type}.${path}`, value);
    const updatedProperties = _.set(properties, productProperty.id, updatedPropertyData);

    this.props.updateProductInstance({
      id: productInstance.id,
      props: { properties: updatedProperties },
      hitApi
    });
  };

  createProductRule = async () => {
    const productRule = await api.create('productRule', {
      orgId: this.props.session.activeOrg.id,
      fromResourceKey: 'productOption',
      toResourceKey: 'productProperty',
      fromIds: [this.props.productOption.id],
      type: 'incompatible'
    });

    this.props.trackProductRules({ productRules: [productRule] });
  };

  createProductOptionClassifier = async () => {
    const newClassifications = {...this.state.classifications, [-1]: -1};

    this.setState({classifications: newClassifications});
  };

  updateClassifier = async ({groupId, classifierId, originalGroupId}) => {
    let updatedClassifications = {...this.state.classifications};

    if (originalGroupId !== undefined && originalGroupId !== groupId) {
      updatedClassifications = _.omit(updatedClassifications, originalGroupId.toString());
    }

    updatedClassifications[groupId] = classifierId;

    this.setState({classifications: updatedClassifications});

    // Only save to database if both groupId and classifierId are not -1 (unassigned)
    if (groupId !== -1 && classifierId !== -1) {
      await this.props.updateProductOption({
        id: this.props.productOption.id,
        props: {classifications: updatedClassifications}
      });

      const updatedProductOption = {...this.props.productOption, classifications: updatedClassifications};
      this.props.trackProductOptions({productOptions: [updatedProductOption]});
    }
  };

  removeClassifier = async (groupId) => {
    const updatedClassifications = _.omit(this.state.classifications, groupId.toString());

    this.setState({classifications: updatedClassifications});

    await this.props.updateProductOption({
      id: this.props.productOption.id,
      props: {classifications: updatedClassifications}
    });

    const updatedProductOption = {...this.props.productOption, classifications: updatedClassifications};
    this.props.trackProductOptions({productOptions: [updatedProductOption]});
  };

  handleReferenceCodeChange = ({value, key}) => {
    var referenceCodes = _.cloneDeep(_.get(this.props.productOption, 'referenceCodes', {}));

    referenceCodes = _.set(referenceCodes, key, value);

    this.props.updateProductOption({id: this.props.productOption.id, props: {referenceCodes}});

    this.props.trackProductOptions({productOptions: [{...this.props.productOption}]});
  };

  handleDataChange = ({value}) => {
    this.setState({data: value});
  };

  updateData({value}) {
    try {
      value = JSON.parse(value);
    } catch (err) {
      console.error('value is not json.');
    }

    this.props.updateProductOption({id: this.props.productOption.id, props: {data: value}});
  }

  handleClassificationsChange = ({value}) => {
    this.setState({classifications: value});
  };

  updateClassifications({value}) {
    try {
      value = JSON.parse(value);
    } catch (err) {
      console.error('value is not json.');
    }

    this.props.updateProductOption({id: this.props.productOption.id, props: {classifications: value}});
  }

  updateEngravings = ({engravings}) => {
    var updatedProperties = _.cloneDeep(_.get(this.props.activeProductInstanceWithData, 'properties', {}));
    var updatedEngravingsPropertyData = _.get(updatedProperties, '27', {});

    _.set(updatedEngravingsPropertyData, 'engravings', engravings);

    _.set(updatedProperties, '27', updatedEngravingsPropertyData);

    this.props.updateProductInstance({id: this.props.productInstance.id, props: {properties: updatedProperties}});
  };

  handleToggleCustomEngravings = () => {
    var { session, activeProductInstanceWithData } = this.props;
    var isOwner = session?.activeOrg?.role === 'owner';

    var enableCustomEngravings = () => {
      var engravings = getCleanedEngravings({
        properties: _.get(activeProductInstanceWithData, 'properties'),
        propertiesDataById: activeProductInstanceWithData?.propertiesDataById,
      });

      this.updateEngravings({engravings});
    };

    if (!isOwner) {
      if (confirm('Custom engravings do not enforce design constraints. All custom designs will be reviewed and may be rejected if they are not feasible for production.\n\nAre you sure you want to proceed?')) enableCustomEngravings();
    } else {
      enableCustomEngravings();
    }
  };

  render() {
    var {productOption, mode, productProperty, inputQuantity, productInstance, productRules, arrayInputProps, activeProductInstanceWithData, productPricingRulesById, productOptionClassifierGroupsById, productOptionClassifiersById} = this.props;

    var engravingsData = _.get(activeProductInstanceWithData, 'properties.27');
    var hasCustomEngravings = _.has(engravingsData, 'engravings');

    var referenceCodeTypes = [
      {title: 'Catalog', key: 'catalog'},
      {title: 'Factory', key: 'factory'},
      {title: 'Machining', key: 'machining'}
    ];

    var productPricingRuleOptions = _.map(productPricingRulesById, ({id, title}) => ({title: `${title} (ID: ${id})`, value: id}));

    return (
      <Popup onClose={this.close}>
        {mode === 'admin' && (
          <View>
            <TextInput
              style={{...K.fonts.pageHeader, paddingHorizontal: K.spacing, paddingTop: 0, backgroundColor: 'white', marginTop: K.spacing, marginBottom: K.spacing}}
              value={productOption.title}
              onChange={({value}) => this.handleTitleChange({value})}
              blurOnEnter
              blurOnSubmit
              multiline
              standardAutoheightStyles
              returnKeyType='done'
            />
            <View style={{marginBottom: K.spacing * 2, flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center'}}>
              <FileInput
                containerStyle ={{height: 64}}
                uploading={this.state.uploading}
                uploadWasSuccessful={this.state.uploadWasSuccessful}
                uploadHasFailed = {this.state.uploadHasFailed}
                handleFilesPicked={(files) => handleFileUpload({files,
                  setState: (newState) => this.setState(newState),
                  api,
                  resource: this.props.productOption,
                  session: this.props.session,
                  resourceKey: 'productOption',
                  trackResource: ({productOptions}) => this.props.trackProductOptions({productOptions})
                })}
              >
                <Image source={this.props.productOption.thumbnailUrl ? {uri: this.props.productOption.thumbnailUrl} : thumbnailPlaceholder} style={{height: 40, width: 40, border: '1px solid black', borderRadius: 25}}/>
              </FileInput>
            </View>
            {this.props.session.activeOrg.role === 'owner' &&
              <LabelledView label={'Product Option Visibility'} styles={{outerView: {marginBottom: K.spacing * 2}}}>
                <PickerInput
                  inline
                  basic
                  style={{height: K.inputHeight, width: '100%'}}
                  buttonStyle={{backgroundColor: K.colors.gray}}
                  options={publishLevelOptions}
                  value={this.state.publishLevel}
                  onChange={({value}) => this.handleProductOptionPropsChange({propKey: 'publishLevel', value})}
                />
              </LabelledView>
            }
            <View style={{}}>
              {_.map(referenceCodeTypes, ({title, key}) => (
                <TextInput
                  key={key}
                  grayLabelledView
                  label={title + ' Reference Code'}
                  labelledViewStyles={{outerView: {marginBottom: K.margin, marginRight: K.margin}}}
                  onChange={({value}) => this.handleReferenceCodeChange({value, key})}
                  value={_.get(productOption, `referenceCodes[${key}]`, '')}
                />
              ))}
            </View>
            <LabelledView gray label={'Pricing Rule'} styles={{outerView: {marginBottom: K.spacing * 2}}}>
              <PickerInput
                showDownArrow
                style={{height: K.inputHeight}}
                buttonStyle={{backgroundColor: K.colors.gray}}
                options={[{title: 'Unassigned', value: -1}, ...productPricingRuleOptions]}
                value={this.state.productPricingRuleId}
                onChange={({value}) => this.handleProductOptionPropsChange({propKey: 'productPricingRuleId', value})}
              />
            </LabelledView>
            <LabelledView label={'Product Option Data'} styles={{innerView: {marginBottom: K.margin}, outerView: {marginBottom: K.spacing * 2}}}>
              <AceEditor
                mode="json"
                theme="github"
                value={this.state.data}
                onChange={(value) => this.handleDataChange({value})}
                onBlur={() => this.updateData({value: this.state.data})}
                height={'50px'}
                setOptions={{
                  useWorker: false,
                  enableBasicAutocompletion: true,
                  enableLiveAutocompletion: true,
                  enableSnippets: true,
                  showLineNumbers: true,
                  tabSize: 2
                }}
              />
            </LabelledView>
            <LabelledView label={'Classifiers'} styles={{innerView: {marginBottom: K.margin}, outerView: {marginBottom: K.spacing * 2}}}>
              {this.state.isLoadingClassifierData ? (<Loading />) : (
                <>
                  {_.map(this.state.classifications, (classifierId, groupId) => {
                    // Get already selected group IDs (excluding current one)
                    const selectedGroupIds = _.without(_.keys(this.state.classifications).map(id => parseInt(id)), parseInt(groupId));

                    const groupOptions = _.chain(productOptionClassifierGroupsById)
                      .reject('deleted')
                      .reject(group => _.includes(selectedGroupIds, group.id)) // Exclude already selected groups
                      .map(group => ({title: `${group.title} (ID: ${group.id})`, value: group.id}))
                      .value();

                    const classifierOptions = _.chain(productOptionClassifiersById)
                      .reject('deleted')
                      .filter(classifier => classifier.productOptionClassifierGroupId === parseInt(groupId))
                      .map(classifier => ({title: `${classifier.title} (ID: ${classifier.id})`, value: classifier.id}))
                      .value();

                    const isClassifierDisabled = parseInt(groupId) === -1;
                    const isInvalid = parseInt(groupId) === -1 || parseInt(classifierId) === -1;

                    return (
                      <View
                        key={`${groupId}-${classifierId}`}
                        style={{
                          marginBottom: K.margin,
                          padding: K.margin,
                          borderColor: isInvalid ? 'red' : 'rgba(0, 0, 0, 0.2)',
                          borderWidth: 1,
                          borderRadius: K.borderRadius,
                          borderStyle: 'solid'
                        }}
                      >
                        <LabelledView gray label={'Classifier Group'} styles={{outerView: {marginBottom: K.margin}}}>
                          <PickerInput
                            showDownArrow
                            style={{height: K.inputHeight}}
                            buttonStyle={{backgroundColor: K.colors.gray}}
                            options={[{title: 'Unassigned', value: -1}, ...groupOptions]}
                            value={parseInt(groupId)}
                            onChange={({value}) => this.updateClassifier({
                              groupId: value,
                              classifierId: -1,
                              originalGroupId: parseInt(groupId)
                            })}
                          />
                        </LabelledView>
                        <LabelledView gray label={'Classifier'} styles={{outerView: {marginBottom: K.margin}}}>
                          <PickerInput
                            showDownArrow
                            style={{height: K.inputHeight}}
                            buttonStyle={{backgroundColor: K.colors.gray}}
                            options={[{title: 'Unassigned', value: -1}, ...classifierOptions]}
                            value={parseInt(classifierId)}
                            onChange={({value}) => this.updateClassifier({
                              groupId: parseInt(groupId),
                              classifierId: value
                            })}
                            enabled={!isClassifierDisabled}
                          />
                        </LabelledView>
                        <View style={{flexDirection: 'row', alignItems: 'center', justifyContent: 'flex-end'}}>
                          {isInvalid && (
                            <Text style={{color: 'red', flex: 1, marginHorizontal: K.margin, fontSize: 12}}>
                              Unsaved until valid options are selected
                            </Text>
                          )}
                          <TouchableOpacity
                            onPress={() => this.removeClassifier(groupId)}
                          >
                            <Image source={trashIcon} style={{...K.defaultIconSize, opacity: 0.5}} />
                          </TouchableOpacity>
                        </View>
                      </View>
                    );
                  })}
                  <Button
                    label={'Add classifier'}
                    dark
                    style={{}}
                    onPress={this.createProductOptionClassifier}
                  />
                </>
              )}
            </LabelledView>
            <LabelledView label={'Rules'} styles={{innerView: {marginBottom: K.margin}, outerView: {marginBottom: K.spacing * 2}}}>
              {!!productRules.length && (
                <>
                  {_.map(productRules, (productRule, index) => <ProductRule key={index} {...{productRule}} />)}
                </>
              )}
              <Button
                label={'Add rule'}
                dark
                style={{}}
                onPress={this.createProductRule}
              />
            </LabelledView>
            <Button
              light
              style={{}}
              onPress={this.archive}
              label={this.props.productOption.archived === 0 ? 'ARCHIVE' : 'UNARCHIVE'}
            />
          </View>
        )}
        {mode === 'order' && (<>
          <View>
            <Text style={{marginLeft: K.spacing, marginTop: K.spacing, opacity: 0.5}}>Engraving text is case-sensitive.</Text>
            {this.props.session.activeOrg.id === 850 && productProperty.type === 'text' && (<View style={{marginLeft: K.spacing, marginBottom: K.spacing * 2, opacity: 0.65}}><GetDisclaimer orgId={this.props.session.activeOrg.id} disclaimerType={'engravingDisclaimer'} /></View>)}

            <View style={{marginBottom: K.spacing}}>
              {(this.props.session.activeOrg.id === 850 && !hasCustomEngravings) && (
                <Button
                  label={'Enable Custom Engravings'}
                  onPress={this.handleToggleCustomEngravings}
                  style={{marginBottom: K.spacing * 2}}
                />
              )}
              {hasCustomEngravings && (
                <View style={{marginBottom: K.spacing}}>
                  {_.times(inputQuantity, mechanismIndex => (
                    <View
                      key={mechanismIndex}
                      style={{display: 'flex', flexDirection: 'column', marginBottom: K.spacing * 1.5}}
                    >
                      {_.times(2, positionIndex => {
                        var engravingLocationKey = positionIndex === 0 ? 'above' : 'below';

                        return (
                          <View key={positionIndex}>
                            {_.times(2, inputIndex => {
                              var type = 'text';
                              var engravingObject = _.get(engravingsData.engravings, `${mechanismIndex}.${engravingLocationKey}[${inputIndex}]`);

                              if (engravingObject) {
                                type = Object.keys(_.get(engravingsData.engravings, `${mechanismIndex}.${engravingLocationKey}[${inputIndex}]`))[0];
                              }

                              return (
                                <CustomEngravingsInput
                                  key={inputIndex}
                                  updateEngravings={this.updateEngravings}
                                  {...{engravingLocationKey, inputIndex, type, activeProductInstanceWithData, engravings: engravingsData.engravings, mechanismIndex, productInstance, productProperty}}
                                />
                              );
                            })}
                          </View>
                        );
                      })}
                    </View>
                  ))}
                </View>
              )}
              {!hasCustomEngravings && (
                <View style={{marginBottom: K.spacing}}>
                  {_.times(inputQuantity, index => {
                    var maxNumberOfInputs = 1000000;
                    var ledIndicatorEnabled;

                    if (productInstance.productId === 1) {
                      var ledIndicatorEnabled = _.get(productInstance, 'properties.85.optionId') === 519;

                      if (ledIndicatorEnabled) maxNumberOfInputs = 1;
                    }

                    if (this.props.session.activeOrg.id === 850) {
                      var engravingHeightIsOverLimit = getEngravingHeightLimit({activeProductInstanceWithData, inputArrayIndex: index, mechanismSpacingRulesById: this.props.mechanismSpacingRulesById});

                      if (engravingHeightIsOverLimit === true) maxNumberOfInputs = 1;
                      if (ledIndicatorEnabled) maxNumberOfInputs = 1;
                    }

                    if (productProperty.type === 'text') {
                      if (arrayInputProps && !_.isEmpty(arrayInputProps)) {
                        return (
                          <View
                            style={{ marginBottom: K.margin * 3}}
                            key={index}
                          >
                            {_.map(_.take(arrayInputProps.inputs, maxNumberOfInputs), (props, inputIndex) => {
                              var backgroundColor = K.colors.gray;

                              if (this.props.session.activeOrg.id === 850) {
                                var {engravingWidthIsOverLimit, engravingWidthIsOverSoftLimit} = getEngravingWidthLimitData({activeProductInstanceWithData, productInstance, productProperty, inputArrayIndex: index, inputIndex, mechanismSpacingRulesById: this.props.mechanismSpacingRulesById});

                                if (engravingWidthIsOverSoftLimit) backgroundColor = '#F5E1BA';
                                if (engravingWidthIsOverLimit) backgroundColor = '#F5BBBA';
                              }

                              return (
                                <TextInput
                                  nativeID={`OrderPageEngravingsPopupInput-${inputIndex}`}
                                  label={inputIndex === 0 ? `${arrayInputProps.label} ${index + 1}${engravingWidthIsOverSoftLimit && !engravingWidthIsOverLimit ? ' (over recommended limit)' : ''}${engravingWidthIsOverLimit ? ' (over limit)' : ''}` : ''}
                                  style={{ backgroundColor, marginBottom: K.spacing}}
                                  key={inputIndex}
                                  placeholder={_.get(props, 'placeholder', '')}
                                  value={_.defaultTo(_.get(productInstance.properties, `${productProperty.id}.text[${index}][${inputIndex}]`, ''), '')}
                                  onBlur={({value}) => this.handleInputChange({value, index, inputIndex, hitApi: true})}
                                  onInput={({value}) => this.handleInputChangeDebounced({value, index, inputIndex, hitApi: false})}
                                />
                              );
                            })}
                          </View>
                        );
                      }
                    }
                    else {
                      return (
                        <TextInput
                          label={`${index + 1}`}
                          style={{marginBottom: K.spacing}}
                          key={index}
                          value={_.get(productInstance.properties, `${productProperty.id}.text[${index}]`, '')}
                          onChange={({value}) => this.handleInputChange({value, index})}
                        />
                      );
                    }
                  })}
                </View>
              )}
            </View>
          </View>
        </>)}
      </Popup>
    );
  }
}

export default connect({
  mapState: (state, ownProps) => {
    var productRules = ownProps.productOption && _.filter(state.resources.productRules.byId, productRule => {
      return productRule.fromResourceKey === 'productOption' && _.includes(productRule.fromIds, ownProps.productOption.id);
    });

    var productPricingRulesById = state.resources.productPricingRules.byId;
    var productOptionClassifierGroupsById = state.resources.productOptionClassifierGroups?.byId || {};
    var productOptionClassifiersById = state.resources.productOptionClassifiers?.byId || {};

    return {
      productRules,
      productPricingRulesById,
      productOptionClassifierGroupsById,
      productOptionClassifiersById,
      mechanismSpacingRulesById: state.resources.mechanismSpacingRules?.byId || {}
    };
  },
  mapDispatch: {
    ..._.pick(resourceActions.productOptions, ['updateProductOption', 'trackProductOptions', 'destroyProductOption']),
    ..._.pick(resourceActions.productInstances, ['updateProductInstance']),
    ..._.pick(resourceActions.productRules, ['trackProductRules']),
    ..._.pick(resourceActions.productOptionClassifierGroups, ['trackProductOptionClassifierGroups']),
    ..._.pick(resourceActions.productOptionClassifiers, ['trackProductOptionClassifiers'])
  }
})(ProductOptionPopup);
